import { ButtonCus } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { PasswordInput } from '@/components/common/Input';
import { ekDomain, PASSWORD_VALIDATE_REGEX, ROUTES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { COMMON_TYPE } from '@/types/enums';
import { SetPasswordPostRequest } from '@/types/http-payload/auth';
import { isPathEk } from '@/utils/routes';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

export default observer(function ResetPasswordPage() {
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const uuid = searchParams.get('uuid') || '';

    // store
    const { authStore: { setPassword, checkUuid }, modalStore: { showAlertModal } } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        newPassword: yup
            .string()
            .trim()
            .required('form_error_validate.required')
            .matches(PASSWORD_VALIDATE_REGEX, 'form_error_validate.format_password'),
        confirmNewPassword: yup
            .string()
            .trim()
            .required('form_error_validate.required')
            .matches(PASSWORD_VALIDATE_REGEX, 'form_error_validate.format_password')
            .oneOf([yup.ref('newPassword')], 'form_error_validate.password_not_match')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            newPassword: '',
            confirmNewPassword: ''
        }
    });

    const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState<boolean>(false);

    // function
    const onSubmit = async (data: FormData) => {
        const checkUuidRes = await flowResult(checkUuid({ uuid }));
        if (checkUuidRes) {
            const dataSubmit: SetPasswordPostRequest = {
                password: data.newPassword,
                uuid,
                type: isPathEk(pathname) ? COMMON_TYPE.EK : COMMON_TYPE.GAS,
                isForgetPassword: true
            };

            const res = await flowResult(setPassword(dataSubmit));
            if (res) {
                showAlertModal({
                    id: 'export-success',
                    type: 'success',
                    content: t('messages.change_password_success'),
                    onClickXIcon: () => {
                        navigate(isPathEk(pathname) ? `/${ekDomain}${ROUTES.signin.href}` : ROUTES.signin.href);
                    }
                });
            }
        }
    };

    return (
        <div>
            <h3 className='title-1 text-center'>{t('words_title.reset_password')}</h3>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className='mt-8'>
                    <div className='flex flex-col gap-4'>
                        <FormField
                            control={form.control}
                            name='newPassword'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel required>{t('field.new_password')}</FormLabel>
                                    <FormControl>
                                        <PasswordInput
                                            type={!showNewPassword ? 'password' : 'text'}
                                            placeholder={t('field.new_password')}
                                            onToggleIcon={() => setShowNewPassword(!showNewPassword)}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='confirmNewPassword'
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel required>{t('field.confirm_new_password')}</FormLabel>
                                    <FormControl>
                                        <PasswordInput
                                            type={!showConfirmNewPassword ? 'password' : 'text'}
                                            placeholder={t('field.confirm_new_password')}
                                            onToggleIcon={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className='flex justify-center mt-8'>
                        <ButtonCus variant='outline' size='md'>
                            {t('button.change')}
                        </ButtonCus>
                    </div>
                </form>
            </Form>
        </div>
    );
});
